import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InlineScannerClaimsComponent } from '../shared/inline-scanner-claims/inline-scanner-claims.component';

/**
 * IT-F2-136 v2 (2026-08-03): per-product grants[] editor. Rendered
 * inline under a product-map row when the operator opens the "..."
 * drawer. Bundle SKUs (single SamCart product → N F2 scanners) live
 * here; each grant carries its own scanner_id + claims + channels
 * because a bundle can legitimately mix Live/Delayed tiers or subscribe
 * to different channels per scanner.
 *
 * Contract with the host: `grants` is bindable — mutations emit
 * `grantsChange` with a fresh array reference. Backend accepts the
 * exact shape via product_map entry.grants[] since IT-F2-136 v1
 * (backend 972e049) and IT-F2-104 v2's updateCommerce fix
 * (16a2196 — the pre-existing String() coerce would have collapsed
 * this to "[object Object]" without that ride-along fix).
 *
 * Claims editor is reused from IT-F2-106 Phase 3d
 * (<app-inline-scanner-claims>) — same schema-driven widgets
 * (stringbool → select, string+enum → select, string → input,
 * bool → checkbox, int → number, array+enum → grouped select or
 * checkbox grid).
 *
 * Channels multi-select is a plain checkbox list against each
 * scanner's Channels[] (F2-ADMIN.Scanners.Channels — array of
 * {id, title, ...} per-scanner notification channels). Scanners
 * with no channels defined show a "no channels" line.
 */

export interface GrantConfig {
  scanner_id: string;
  claims?: Record<string, any>;
  channels?: string[];
}

@Component({
  standalone: true,
  selector: 'app-product-map-grants-drawer',
  imports: [CommonModule, InlineScannerClaimsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="mt-2 mb-1 pl-4 border-l-2 border-emerald-500/40 bg-gray-50 dark:bg-app-black-100/40 rounded-r px-3 py-2 space-y-3">
  <div class="flex items-center justify-between">
    <div class="text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
      <i class="fa-solid fa-layer-group mr-1 text-emerald-500"></i>
      Grants ({{ grants.length }})
    </div>
    <div class="flex items-center gap-2">
      <button type="button"
              class="text-[10px] uppercase tracking-wide border border-emerald-500 text-emerald-600 dark:text-emerald-400 rounded px-2 py-0.5 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
              (click)="addGrant()">
        <i class="fa-solid fa-plus mr-1"></i>Add grant
      </button>
    </div>
  </div>

  <div *ngIf="grants.length === 0" class="text-[11px] italic text-gray-500 dark:text-gray-400 py-2">
    No grants yet. Add one per scanner this product should entitle the buyer to. Use this for bundle SKUs that grant multiple scanners (e.g. Trader X Pro → all 4 op-* scanners). For a single-scanner product, use the Target dropdown above instead.
  </div>

  <div *ngFor="let g of grants; let i = index; trackBy: trackByIndex"
       class="border border-gray-200 dark:border-[#434863] rounded p-2 space-y-2 bg-white dark:bg-app-black-100/60">
    <!-- Grant header: scanner picker + remove -->
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 whitespace-nowrap">Grant {{ i + 1 }}:</span>
        <!-- [value] on native <select> is unreliable when the target
             <option> renders via *ngFor in the SAME CD pass (options
             not yet in the DOM when the select's value is set → falls
             back to the first option). [selected] on each option is
             the OnPush-safe pattern. -->
        <select class="flex-1 text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                (change)="onScannerChange(i, $any($event.target).value)">
          <option value="" [selected]="!g.scanner_id">— pick a scanner —</option>
          <option *ngFor="let s of scannerCatalog"
                  [value]="s.id"
                  [selected]="g.scanner_id === s.id"
                  [ngStyle]="s.disabled ? {color: '#dc2626'} : null">
            {{ s.name }} ({{ s.id }}){{ s.disabled ? ' — disabled' : '' }}
          </option>
          <!-- Preserve an out-of-catalog scanner_id so a stale entry doesn't silently vanish on save. -->
          <option *ngIf="g.scanner_id && !catalogHas(g.scanner_id)"
                  [value]="g.scanner_id"
                  [selected]="true"
                  [ngStyle]="{color: '#dc2626'}">
            {{ g.scanner_id }} (not in customer's current scanner list)
          </option>
        </select>
      </div>
      <button type="button"
              class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-xs px-1"
              (click)="removeGrant(i)"
              title="Remove this grant">
        <i class="fa-solid fa-trash"></i>
      </button>
    </div>

    <!-- Claims editor (reuses IT-F2-106 Phase 3d InlineScannerClaimsComponent) -->
    <div *ngIf="g.scanner_id && scannerDocFor(g.scanner_id) as sdoc" class="pl-4">
      <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
        <i class="fa-solid fa-tag mr-1"></i>Claims
      </div>
      <div *ngIf="hasClaimsSchema(sdoc); else noSchema">
        <app-inline-scanner-claims
          [scanner]="sdoc"
          [customer]="customer"
          [entry]="claimsEntryFor(i)"
          (entryChange)="onClaimsChange(i, $event)">
        </app-inline-scanner-claims>
      </div>
      <ng-template #noSchema>
        <div class="text-[11px] italic text-gray-500 dark:text-gray-400">
          No claims_schema defined on this scanner — nothing to configure.
        </div>
      </ng-template>
    </div>

    <!-- Channels multi-select -->
    <div *ngIf="g.scanner_id && scannerDocFor(g.scanner_id) as sdoc" class="pl-4">
      <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
        <i class="fa-solid fa-bell mr-1"></i>Channels
      </div>
      <ng-container *ngIf="channelsFor(sdoc) as chans">
        <div *ngIf="chans.length === 0" class="text-[11px] italic text-gray-500 dark:text-gray-400">
          No channels defined on this scanner.
        </div>
        <div *ngIf="chans.length > 0" class="flex flex-wrap gap-3">
          <label *ngFor="let ch of chans; trackBy: trackByChannelId"
                 class="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200 cursor-pointer">
            <input type="checkbox"
                   class="rounded border-gray-300 dark:border-[#434863]"
                   [checked]="isChannelChecked(i, ch.id)"
                   (change)="toggleChannel(i, ch.id, $any($event.target).checked)" />
            <span>{{ ch.title || ch.id }}</span>
          </label>
        </div>
      </ng-container>
    </div>
  </div>
</div>
  `,
})
export class ProductMapGrantsDrawerComponent {
  @Input() grants: GrantConfig[] = [];
  @Output() grantsChange = new EventEmitter<GrantConfig[]>();

  // Same shape as ProductMapEditor's scannerCatalog — plus the full docs
  // keyed by id for claims_schema + channels lookup.
  @Input() scannerCatalog: Array<{ id: string; name: string; disabled: boolean }> = [];
  @Input() scannerDocs: Record<string, any> = {};

  // Customer doc — fallback claims_schema source per the shared editor's
  // resolution rule.
  @Input() customer: any = null;

  // ── Grant CRUD ─────────────────────────────────────────────────────

  addGrant() {
    this.grants = [...this.grants, { scanner_id: '', claims: {}, channels: [] }];
    this._emit();
  }

  removeGrant(i: number) {
    if (i < 0 || i >= this.grants.length) return;
    const copy = this.grants.slice();
    copy.splice(i, 1);
    this.grants = copy;
    this._emit();
  }

  onScannerChange(i: number, sid: string) {
    const copy = this.grants.slice();
    copy[i] = { ...copy[i], scanner_id: String(sid || '').trim() };
    // Changing scanner resets claims to seed defaults on next render;
    // channels wiped because they're per-scanner.
    copy[i].claims = {};
    copy[i].channels = [];
    this.grants = copy;
    this._emit();
  }

  onClaimsChange(i: number, entry: any) {
    if (i < 0 || i >= this.grants.length) return;
    const copy = this.grants.slice();
    // Strip the schema-seeded `scanner` field from the claims blob —
    // InlineScannerClaims writes it as a template default but the grant
    // shape already carries scanner_id at the top level. Prevents
    // double-storage + a redundant round-trip on save.
    const { scanner: _drop, ...rest } = (entry || {});
    copy[i] = { ...copy[i], claims: rest };
    this.grants = copy;
    this._emit();
  }

  toggleChannel(i: number, chId: string, on: boolean) {
    if (i < 0 || i >= this.grants.length) return;
    const copy = this.grants.slice();
    const cur = Array.isArray(copy[i].channels) ? (copy[i].channels || []).slice() : [];
    const idx = cur.indexOf(chId);
    if (on && idx < 0) cur.push(chId);
    else if (!on && idx >= 0) cur.splice(idx, 1);
    copy[i] = { ...copy[i], channels: cur };
    this.grants = copy;
    this._emit();
  }

  isChannelChecked(i: number, chId: string): boolean {
    const g = this.grants[i];
    return !!g && Array.isArray(g.channels) && g.channels.indexOf(chId) >= 0;
  }

  // ── Lookups ────────────────────────────────────────────────────────

  catalogHas(id: string): boolean {
    return !!this.scannerCatalog.find((s) => s.id === id);
  }

  scannerDocFor(id: string): any | null {
    return (this.scannerDocs && this.scannerDocs[id]) || null;
  }

  hasClaimsSchema(sdoc: any): boolean {
    const schema = sdoc?.claims_schema;
    if (schema && Array.isArray(schema.fields) && schema.fields.length > 0) return true;
    const cust = this.customer?.claims_schema;
    return !!(cust && Array.isArray(cust.fields) && cust.fields.length > 0);
  }

  channelsFor(sdoc: any): Array<{ id: string; title?: string }> {
    const raw = sdoc?.Channels || sdoc?.channels || [];
    if (!Array.isArray(raw)) return [];
    const out: Array<{ id: string; title?: string }> = [];
    for (const c of raw) {
      if (!c) continue;
      if (typeof c === 'string') {
        if (c) out.push({ id: c, title: c });
      } else {
        const id = String(c.id ?? c.Id ?? '');
        if (id) out.push({ id, title: String(c.title ?? c.name ?? c.id ?? '') });
      }
    }
    return out;
  }

  claimsEntryFor(i: number): any {
    // InlineScannerClaims mutates the entry object in place + emits a
    // shallow clone. We give it a fresh view of the current claims so
    // the seed-on-mount branch doesn't clobber prior state.
    const g = this.grants[i];
    return g && g.claims && typeof g.claims === 'object' ? { ...g.claims } : {};
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByChannelId(_: number, ch: { id: string }): string {
    return ch.id;
  }

  private _emit() {
    this.grantsChange.emit(this.grants);
  }
}
